import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Explore from './components/Explore';
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext';
import { LocationProvider } from "./contexts/LocationContext";
import WithUserSignedIn from "./utils/WithUserSignedIn";
import FloatingContactButton from './components/FloatingContactButton';
import MobileTabBar from './components/MobileTabBar';
import ScrollToTop from './components/ScrollToTop';
import LoadingScreen from './components/LoadingScreen';
import useSafeAreaVars from './hooks/useSafeAreaVars';

// Lazy-loaded: not needed for the initial landing page, so they ship as
// separate chunks instead of bloating the main bundle every visitor downloads.
const Login = lazy(() => import('./components/Accounts/Login'));
const Signup = lazy(() => import('./components/Accounts/Signup'));
const ForgotPassword = lazy(() => import('./components/Accounts/ForgotPassword'));
const ViewProfile = lazy(() => import('./components/Accounts/ViewProfile'));
const FieldDetail = lazy(() => import('./components/FieldDetail'));
const BookingCheckout = lazy(() => import('./components/BookingCheckout'));
const BookingConfirmation = lazy(() => import('./components/BookingConfirmation'));
const ManageListingPage = lazy(() => import('./components/ManageListingPage'));
const AnalyticsPage = lazy(() => import('./components/AnalyticsPage'));
const OrganizationPage = lazy(() => import('./components/OrganizationPage'));
const CreateClubPage = lazy(() => import('./components/CreateClubPage'));
const OperatorAvailabilityManager = lazy(() => import('./components/OperatorAvailabilityManager'));
const OperatorTeamPage = lazy(() => import('./components/OperatorTeamPage'));
const MyBookings = lazy(() => import('./components/MyBookings'));
const OperatorBookings = lazy(() => import('./components/OperatorBookings'));
const OperatorBookingDetail = lazy(() => import('./components/OperatorBookingDetail'));
const PublicOperatorProfile = lazy(() => import('./components/PublicOperatorProfile'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));

/* 

App.js in React is responsible for the frontend configurations

Responsible for

1. Handling UI Logic
2. Routing
3. Rendering

*/
function App() {
  useSafeAreaVars();
  return (
    <AuthProvider>
      <ThemeProvider>
      <LocationProvider>
      <Router>
        <div>
          <ScrollToTop />
          <Suspense fallback={<LoadingScreen fullPage />}>
          <Routes>
            <Route path="/" element={<Explore />} /> {/* Default route for the base URL */}
            <Route path="/login" element={<Login />} /> {/* Route for the login page */}
            <Route path="/signup" element={<Signup />} /> {/* Route for the signup page */}
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route path="/profile" element={
              <WithUserSignedIn>
                <ViewProfile />
              </WithUserSignedIn>
              } />

            <Route path="/field/:id" element={<FieldDetail />} />
            <Route path="/booking-checkout" element={<BookingCheckout />} />
            <Route path="/booking-confirmation" element={<BookingConfirmation />} />
            <Route path="/create-listing" element={<ManageListingPage />} />
            <Route path="/edit-listing/:listingID" element={<ManageListingPage />} />
            <Route path="/operator-profile" element={<AnalyticsPage />} />
            <Route path="/organization" element={<OrganizationPage />} />
            <Route path="/operator/:operatorID" element={<PublicOperatorProfile />} />
            <Route path="/create-club" element={<CreateClubPage />} />
            <Route path="/operator/:operatorID/team" element={
              <WithUserSignedIn>
                <OperatorTeamPage />
              </WithUserSignedIn>
              } />
            <Route path="/operator/:operatorID/listings/:listingID/availability" element={
              <WithUserSignedIn>
                <OperatorAvailabilityManager />
              </WithUserSignedIn>
              } />
            <Route path="/my-bookings" element={
              <WithUserSignedIn>
                <MyBookings />
              </WithUserSignedIn>
              } />
            <Route path="/bookings" element={
              <WithUserSignedIn>
                <OperatorBookings />
              </WithUserSignedIn>
              } />
            <Route path="/bookings/:bookingID" element={
              <WithUserSignedIn>
                <OperatorBookingDetail />
              </WithUserSignedIn>
              } />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>

          <FloatingContactButton />
          <MobileTabBar />
        </div>
      </Router>
      </LocationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
